import { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

export type Periodo = 'hoy' | 'semana' | 'mes' | 'anio';

export interface DashboardKPIs {
  // Fila 1 - Dinero
  ventasDia: number;
  ventasMes: number;
  ventasMesAnterior: number;
  variacionMes: number;
  porCobrar: number;
  totalVencido: number;
  cobrosHoy: number;
  // Fila 2 - Operación
  pedidosEnCalle: number;
  entregasCompletadasHoy: number;
  entregasPendientesHoy: number;
  pedidosPorSurtir: number;
  // Fila 3 - Alertas
  creditoExcedido: number;
  stockBajo: number;
  pedidosSinAutorizar24h: number;
  facturasVencenSemana: number;
  pagosPorValidar: number;
  preciosRevisionPendientes: number;
  lotesVencidos: number;
  fumigacionesVencidas: number;
  anticiposPendientes: number;
  creditosProveedores: number;
  entregasComprasAtrasadas: number;
  entregasEnDescarga: number;
  entregasEnDescargaDetalle: any[];
  entregasCompletadasHoyDetalle: any[];
}

export interface AlertaUrgente {
  tipo: 'pedidos_sin_autorizar' | 'chofer_sin_gps' | 'stock_cero' | 'credito_excedido' | 'pagos_por_validar' | 'precios_por_revisar' | 'lotes_vencidos' | 'fumigaciones_vencidas' | 'anticipos_pendientes' | 'creditos_proveedores' | 'entregas_compras_atrasadas';
  cantidad: number;
  detalle?: string;
  ruta: string;
  botonTexto: string;
}

export interface TopProducto {
  id: string;
  nombre: string;
  cantidadVendida: number;
  montoTotal: number;
}

export interface TopCliente {
  id: string;
  nombre: string;
  totalPesos: number;
  numPedidos: number;
}

export interface ResumenFinanciero {
  ticketPromedio: number;
  clientesNuevosMes: number;
  clientesInactivos: number;
  tasaEntregasExitosas: number;
}

export interface DashboardData {
  kpis: DashboardKPIs;
  alertas: AlertaUrgente[];
  topProductos: TopProducto[];
  topClientes: TopCliente[];
  resumenFinanciero: ResumenFinanciero;
}

const EMPTY_KPIS: DashboardKPIs = {
  ventasDia: 0, ventasMes: 0, ventasMesAnterior: 0, variacionMes: 0,
  porCobrar: 0, totalVencido: 0, cobrosHoy: 0, pedidosEnCalle: 0,
  entregasCompletadasHoy: 0, entregasPendientesHoy: 0, pedidosPorSurtir: 0,
  creditoExcedido: 0, stockBajo: 0, pedidosSinAutorizar24h: 0, facturasVencenSemana: 0, pagosPorValidar: 0, preciosRevisionPendientes: 0,
  lotesVencidos: 0, fumigacionesVencidas: 0,
  anticiposPendientes: 0, creditosProveedores: 0, entregasComprasAtrasadas: 0, entregasEnDescarga: 0, entregasEnDescargaDetalle: [], entregasCompletadasHoyDetalle: [],
};

export function useDashboardData(periodo: Periodo = 'mes') {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const now = new Date();
      const hoy = now.toISOString().split('T')[0];
      const inicioHoy = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
      const inicioMes = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const inicioMesAnterior = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString();
      const finMesAnterior = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59).toISOString();
      const hace24h = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const finSemana = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const hace30Dias = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();

      // Batch all independent queries
      const [
        ventasDiaRes, ventasMesRes, ventasMesAntRes,
        clientesSaldoRes,
        facturasVencidasRes,
        pedidosEnCalleRes,
        pedidosPorSurtirRes,
        pedidosSinAutRes,
        stockBajoRes, stockCeroRes,
        facturasVencenSemanaRes,
        clientesNuevosRes,
        topProductosRes,
        topClientesRes,
        entregasHoyRes,
        cobrosHoyRes,
        pagosPorValidarRes,
        preciosRevisionRes,
        lotesVencidosRes,
        fumigacionesVencidasRes,
        anticiposRes,
        creditosProvRes,
        entregasComprasAtrasadasRes,
        entregasEnDescargaRes,
        entregasRecibidasHoyRes,
      ] = await Promise.all([
        // Ventas del día
        supabase.from("pedidos").select("total").gte("created_at", inicioHoy).in("status", ["entregado", "en_ruta"]),
        // Ventas del mes
        supabase.from("pedidos").select("total").gte("created_at", inicioMes).in("status", ["entregado", "en_ruta"]),
        // Ventas mes anterior
        supabase.from("pedidos").select("total").gte("created_at", inicioMesAnterior).lte("created_at", finMesAnterior).in("status", ["entregado", "en_ruta"]),
        // Clientes con saldo
        supabase.from("clientes").select("id, saldo_pendiente, limite_credito").gt("saldo_pendiente", 0),
        // Facturas vencidas (timbradas + no pagadas)
        (supabase as any).from("facturas").select("total, fecha_vencimiento").lt("fecha_vencimiento", hoy).eq("cfdi_estado", "timbrada").eq("pagada", false),
        // Pedidos en calle
        supabase.from("pedidos").select("id", { count: "exact", head: true }).eq("status", "en_ruta"),
        // Pedidos por surtir
        supabase.from("pedidos").select("id", { count: "exact", head: true }).eq("status", "pendiente"),
        // Pedidos sin autorizar > 24h
        supabase.from("pedidos").select("id", { count: "exact", head: true }).eq("status", "por_autorizar").lt("created_at", hace24h),
        // Stock bajo (usa VIEW que compara columna vs columna server-side)
        supabase.from("productos_stock_bajo" as any).select("id", { count: "exact", head: true }),
        // Stock = 0
        supabase.from("productos").select("id", { count: "exact", head: true }).eq("stock_actual", 0).eq("activo", true),
        // Facturas que vencen esta semana (timbradas + no pagadas)
        (supabase as any).from("facturas").select("id", { count: "exact", head: true }).gte("fecha_vencimiento", hoy).lte("fecha_vencimiento", finSemana).eq("cfdi_estado", "timbrada").eq("pagada", false),
        // Clientes nuevos del mes
        supabase.from("clientes").select("id", { count: "exact", head: true }).gte("created_at", inicioMes),
        // Top 10 productos del mes - get details from pedidos_detalles
        supabase.from("pedidos_detalles").select("producto_id, cantidad, subtotal, productos(nombre), pedidos!inner(status, created_at)").gte("pedidos.created_at", inicioMes).in("pedidos.status", ["entregado", "en_ruta"]),
        // Top 10 clientes del mes
        supabase.from("pedidos").select("cliente_id, total, clientes(nombre)").gte("created_at", inicioMes).in("status", ["entregado", "en_ruta"]),
        // Entregas de hoy
        supabase.from("rutas").select("id, status, entregas(id, status_entrega)").eq("fecha_ruta", hoy),
        // Cobros de hoy
        supabase.from("pagos_cliente").select("monto_total").gte("fecha_registro", inicioHoy).neq("status", "rechazado"),
        // Pagos por validar
        supabase.from("pagos_cliente").select("id", { count: "exact", head: true }).eq("status", "pendiente").eq("requiere_validacion", true),
        // Precios revision pendientes
        (supabase as any).from("productos_revision_precio").select("id", { count: "exact", head: true }).in("status", ["pendiente", "parcial"]),
        // Lotes vencidos (caducidad < hoy con stock)
        supabase.from("inventario_lotes").select("id", { count: "exact", head: true }).lt("fecha_caducidad", hoy).gt("cantidad_disponible", 0),
        // Fumigaciones vencidas
        supabase.from("productos").select("id, fecha_ultima_fumigacion").eq("requiere_fumigacion", true).eq("activo", true).gt("stock_actual", 0),
        // Anticipos pendientes (OCs anticipadas pagadas sin recibir)
        supabase.from("ordenes_compra").select("total, monto_pagado").eq("tipo_pago", "anticipado").eq("status_pago", "pagado").not("status", "in", "(recibida,completada,cancelada)"),
        // Créditos a favor de proveedores
        (supabase as any).from("proveedor_creditos_pendientes").select("monto_total").eq("status", "pendiente"),
        // Entregas de compras atrasadas (fecha pasada, status programada)
        supabase.from("ordenes_compra_entregas").select("id", { count: "exact", head: true }).eq("status", "programada").lt("fecha_programada", hoy),
        // Entregas en descarga (en curso)
        supabase.from("ordenes_compra_entregas").select(`id, numero_entrega, llegada_registrada_en, trabajando_desde, nombre_chofer_proveedor, placas_vehiculo, cantidad_bultos, orden_compra:ordenes_compra!inner(id, folio, proveedor:proveedores(nombre))`).eq("status", "en_descarga"),
        // Entregas completadas hoy
        // Entregas completadas hoy (UTC-6 México: 00:00 MX = 06:00 UTC)
        supabase.from("ordenes_compra_entregas").select(`id, numero_entrega, llegada_registrada_en, recepcion_finalizada_en, nombre_chofer_proveedor, cantidad_bultos, orden_compra:ordenes_compra!inner(folio, proveedor:proveedores(nombre))`).eq("status", "recibida").gte("recepcion_finalizada_en", hoy + "T06:00:00Z"),
      ]);

      // KPIs calculations
      const ventasDia = ventasDiaRes.data?.reduce((s, p) => s + (p.total || 0), 0) || 0;
      const ventasMes = ventasMesRes.data?.reduce((s, p) => s + (p.total || 0), 0) || 0;
      const ventasMesAnterior = ventasMesAntRes.data?.reduce((s, p) => s + (p.total || 0), 0) || 0;
      const variacionMes = ventasMesAnterior > 0 ? ((ventasMes - ventasMesAnterior) / ventasMesAnterior) * 100 : 0;
      const porCobrar = clientesSaldoRes.data?.reduce((s, c) => s + (c.saldo_pendiente || 0), 0) || 0;
      const totalVencido = facturasVencidasRes.data?.reduce((s: number, f: any) => s + (f.total || 0), 0) || 0;
      const creditoExcedido = clientesSaldoRes.data?.filter((c: any) => c.limite_credito && c.saldo_pendiente > c.limite_credito).length || 0;

      // Entregas hoy
      let entregasCompletadasHoy = 0;
      let entregasPendientesHoy = 0;
      entregasHoyRes.data?.forEach((ruta: any) => {
        ruta.entregas?.forEach((e: any) => {
          if (e.status_entrega === 'entregado' || e.status_entrega === 'completo') {
            entregasCompletadasHoy++;
          } else {
            entregasPendientesHoy++;
          }
        });
      });

      const cobrosHoy = cobrosHoyRes.data?.reduce((s: number, p: any) => s + (Number(p.monto_total) || 0), 0) || 0;

      // Compute fumigaciones vencidas count from data
      const nowDate = new Date();
      const fumVencidasCount = (fumigacionesVencidasRes.data || []).filter((p: any) => {
        if (!p.fecha_ultima_fumigacion) return true;
        const venc = new Date(p.fecha_ultima_fumigacion);
        venc.setMonth(venc.getMonth() + 6);
        return venc < nowDate;
      }).length;

      const kpis: DashboardKPIs = {
        ventasDia, ventasMes, ventasMesAnterior, variacionMes,
        porCobrar, totalVencido, cobrosHoy,
        pedidosEnCalle: pedidosEnCalleRes.count || 0,
        entregasCompletadasHoy, entregasPendientesHoy,
        pedidosPorSurtir: pedidosPorSurtirRes.count || 0,
        creditoExcedido,
        stockBajo: stockBajoRes.count || 0,
        pedidosSinAutorizar24h: pedidosSinAutRes.count || 0,
        facturasVencenSemana: facturasVencenSemanaRes.count || 0,
        pagosPorValidar: pagosPorValidarRes.count || 0,
        preciosRevisionPendientes: (preciosRevisionRes as any)?.count || 0,
        lotesVencidos: lotesVencidosRes.count || 0,
        fumigacionesVencidas: fumVencidasCount,
        anticiposPendientes: (anticiposRes.data || []).reduce((s: number, oc: any) => s + (oc.monto_pagado || 0), 0),
        creditosProveedores: (creditosProvRes.data || []).reduce((s: number, c: any) => s + (c.monto_total || 0), 0),
        entregasComprasAtrasadas: entregasComprasAtrasadasRes.count || 0,
        entregasEnDescarga: (entregasEnDescargaRes.data as any[])?.length || 0,
        entregasEnDescargaDetalle: (entregasEnDescargaRes.data as any[]) || [],
        entregasCompletadasHoyDetalle: (entregasRecibidasHoyRes.data as any[]) || [],
      };

      // Alertas urgentes
      const alertas: AlertaUrgente[] = [];
      if ((pedidosSinAutRes.count || 0) > 0) {
        alertas.push({ tipo: 'pedidos_sin_autorizar', cantidad: pedidosSinAutRes.count || 0, ruta: '/pedidos?tab=por-autorizar', botonTexto: 'Ir a autorizar' });
      }
      if ((stockCeroRes.count || 0) > 0) {
        alertas.push({ tipo: 'stock_cero', cantidad: stockCeroRes.count || 0, ruta: '/compras?tab=sugerencias', botonTexto: 'Ver sugerencias' });
      }
      if (creditoExcedido > 0) {
        alertas.push({ tipo: 'credito_excedido', cantidad: creditoExcedido, ruta: '/clientes', botonTexto: 'Ver cobranza' });
      }
      if ((pagosPorValidarRes.count || 0) > 0) {
        alertas.push({ tipo: 'pagos_por_validar', cantidad: pagosPorValidarRes.count || 0, ruta: '/secretaria', botonTexto: 'Validar pagos' });
      }
      const preciosCount = (preciosRevisionRes as any)?.count || 0;
      if (preciosCount > 0) {
        alertas.push({ tipo: 'precios_por_revisar', cantidad: preciosCount, ruta: '/precios', botonTexto: 'Revisar ahora' });
      }
      if ((lotesVencidosRes.count || 0) > 0) {
        alertas.push({ tipo: 'lotes_vencidos', cantidad: lotesVencidosRes.count || 0, ruta: '/almacen-tablet', botonTexto: 'Ver en almacén' });
      }
      if (fumVencidasCount > 0) {
        alertas.push({ tipo: 'fumigaciones_vencidas', cantidad: fumVencidasCount, ruta: '/almacen-tablet', botonTexto: 'Ver fumigaciones' });
      }
      if (kpis.anticiposPendientes > 0) {
        alertas.push({ tipo: 'anticipos_pendientes', cantidad: kpis.anticiposPendientes, ruta: '/compras?tab=adeudos', botonTexto: 'Ver anticipos' });
      }
      if (kpis.creditosProveedores > 0) {
        alertas.push({ tipo: 'creditos_proveedores', cantidad: kpis.creditosProveedores, ruta: '/compras?tab=devoluciones-faltantes', botonTexto: 'Ver créditos' });
      }
      if (kpis.entregasComprasAtrasadas > 0) {
        alertas.push({ tipo: 'entregas_compras_atrasadas', cantidad: kpis.entregasComprasAtrasadas, ruta: '/almacen-tablet', botonTexto: 'Ver entregas' });
      }

      const prodMap = new Map<string, TopProducto>();
      topProductosRes.data?.forEach((d: any) => {
        const pid = d.producto_id;
        const existing = prodMap.get(pid);
        if (existing) {
          existing.cantidadVendida += d.cantidad || 0;
          existing.montoTotal += d.subtotal || 0;
        } else {
          prodMap.set(pid, {
            id: pid,
            nombre: (d.productos as any)?.nombre || 'Producto',
            cantidadVendida: d.cantidad || 0,
            montoTotal: d.subtotal || 0,
          });
        }
      });
      const topProductos = Array.from(prodMap.values()).sort((a, b) => b.montoTotal - a.montoTotal).slice(0, 10);

      // Top clientes aggregation
      const cliMap = new Map<string, TopCliente>();
      topClientesRes.data?.forEach((p: any) => {
        const cid = p.cliente_id;
        const existing = cliMap.get(cid);
        if (existing) {
          existing.totalPesos += p.total || 0;
          existing.numPedidos++;
        } else {
          cliMap.set(cid, {
            id: cid,
            nombre: (p.clientes as any)?.nombre || 'Cliente',
            totalPesos: p.total || 0,
            numPedidos: 1,
          });
        }
      });
      const topClientes = Array.from(cliMap.values()).sort((a, b) => b.totalPesos - a.totalPesos).slice(0, 10);

      // Resumen financiero
      const numPedidosMes = ventasMesRes.data?.length || 0;
      const ticketPromedio = numPedidosMes > 0 ? ventasMes / numPedidosMes : 0;

      // Clientes inactivos: have orders but none in last 30 days
      // We'll use a simpler approach: count clients with saldo but no recent orders
      const { count: totalClientesActivos } = await supabase
        .from("clientes").select("id", { count: "exact", head: true }).eq("activo", true);
      const { data: clientesConPedidosRecientes } = await supabase
        .from("pedidos").select("cliente_id").gte("created_at", hace30Dias).in("status", ["entregado", "en_ruta", "pendiente", "por_autorizar"]);
      const clientesActivosRecientes = new Set(clientesConPedidosRecientes?.map(p => p.cliente_id)).size;
      const clientesInactivos = Math.max((totalClientesActivos || 0) - clientesActivosRecientes, 0);

      const totalEntregasHoy = entregasCompletadasHoy + entregasPendientesHoy;
      const tasaEntregasExitosas = totalEntregasHoy > 0 ? (entregasCompletadasHoy / totalEntregasHoy) * 100 : 0;

      setData({
        kpis,
        alertas,
        topProductos,
        topClientes,
        resumenFinanciero: {
          ticketPromedio,
          clientesNuevosMes: clientesNuevosRes.count || 0,
          clientesInactivos,
          tasaEntregasExitosas,
        },
      });
      setLastRefresh(new Date());
    } catch (error) {
      console.error("Error loading dashboard data:", error);
    } finally {
      setLoading(false);
    }
  }, [periodo]);

  useEffect(() => {
    setLoading(true);
    fetchData();
    intervalRef.current = setInterval(fetchData, 60000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchData]);

  return { data, loading, lastRefresh, refresh: fetchData };
}
